import { Card } from "@rothoblaas/design-system";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";
import { noticeText } from "../notices/noticeText";
import { actionTargetKey } from "../notices/noticeTargets";
import { useNoticeTarget, useNotices } from "../notices/NoticesContext";
import type { ReactNode } from "react";
import classNames from "classnames";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import type { OperationMode } from "../../../../../http/lock_calculator/types";
import { LC_IMG } from "../../constants";
import styles from "../../LockCalculator.module.css";
import type { LockCalculatorFormValues } from "../../model/form";
import { getStructuralVisibility, hasRealLoadValue } from "../../model/structuralVisibility";
import { JoistTopFields } from "./JoistTopFields";
import { buildLateralResistanceOptions, isLateralOptionSelected, nextLateralConfig } from "../../model/lateralResistanceOptions";
import { TimberForm } from "./TimberForm";

const resistanceImages: Record<number, string | null> = {
  0: `${LC_IMG}/column-notch.png`,
  1: `${LC_IMG}/primary-beam-notch.png`,
  2: `${LC_IMG}/secondary-beam-notch.png`,
  3: `${LC_IMG}/inclined-screws.png`,
  4: `${LC_IMG}/lockstop.png`,
};

/** The picture of a configuration; the exposed connection (no configuration) has none yet. */
const imageFor = (value: number | null): string | null => (value === null ? null : (resistanceImages[value] ?? null));

type Props = {
  readOnly: boolean;
  onOpenHelp: (text: string, image?: string) => void;
  goToConnectorsStep: () => void;
  /** After "generate all": connectors step on the left, generated configurations on the right. */
  onGeneratedAll: () => void;
  onTriggerOperation: (operationMode: OperationMode) => Promise<void>;
  isTriggeringOperation: boolean;
  stepper: ReactNode;
  /** False while a required field of this or an earlier step is empty: both buttons lead into Connectors. */
  canContinue: boolean;
};

export const StructureStep = ({
  readOnly,
  onOpenHelp,
  goToConnectorsStep,
  onGeneratedAll,
  onTriggerOperation,
  isTriggeringOperation,
  stepper,
  canContinue,
}: Props) => {
  const { translation: t } = useI18n();
  const { automaticBlock } = useNotices();
  const generateAllRef = useNoticeTarget(actionTargetKey("generateAll"));
  const form = useFormContext<LockCalculatorFormValues>();
  const values = useWatch({ control: form.control }) as LockCalculatorFormValues;
  const hasLateralLoad = hasRealLoadValue(values?.loads?.lateral);
  const visibility = getStructuralVisibility(
    values?.loads ?? { vertical: null, uplift: null, axial: null, lateral: null },
  );

  const handleGenerateAll = async () => {
    await onTriggerOperation("generateAll");
    onGeneratedAll();
  };

  return (
    <>
      <Card style={{ minHeight: 400 }}
        accentColor="var(--accent-base, var(--red))"
        shadow
        className={`${styles.span12} ${styles.compactCard} ${styles.stepCard}`}
      >
        {stepper}
        {/* ── Structural Configuration ── */}
        {/* ── Timber Configuration ── */}
        <TimberForm readOnly={readOnly} onOpenHelp={onOpenHelp} />
        <JoistTopFields readOnly={readOnly} onOpenHelp={onOpenHelp} />
        <div className={styles.compactFormStack}>
          <Controller
            name="lateralResistanceConfig"
            control={form.control}
            render={({ field }) => (
              <FormField name="lateralResistanceConfig"
                label={hasLateralLoad ? t.lockCalculatorModule?.structural?.configType : t.lockCalculatorModule?.structural?.connectionPosition}
                error={form.formState.errors.lateralResistanceConfig}
                isOptional
                showHelp={
                  hasLateralLoad
                    ? {
                        tooltip: t.lockCalculatorModule?.helpTexts?.lateralResistanceConfig,
                        onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.lateralResistanceConfig),
                      }
                    : undefined
                }
              >
                <div className={styles.imageSelectGrid}>
                  {buildLateralResistanceOptions(t, values?.headerMember?.orientation, hasLateralLoad).map((option) => (
                    <button
                      key={option.value ?? "exposed"}
                      type="button"
                      className={classNames(styles.imageSelectOption, {
                        [styles.imageSelectOptionSelected]: isLateralOptionSelected(option, field.value, hasLateralLoad),
                      })}
                      aria-pressed={isLateralOptionSelected(option, field.value, hasLateralLoad)}
                      disabled={readOnly}
                      title={option.label}
                      onClick={() => field.onChange(nextLateralConfig(option, field.value))}
                    >
                      {imageFor(option.value) ? (
                        <img
                          className={styles.imageSelectImage}
                          src={imageFor(option.value)!}
                          alt={option.label}
                        />
                      ) : (
                        <svg
                          className={styles.imageSelectSvg}
                          viewBox="0 0 70 48"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="8" y="14" width="48" height="20" rx="3" stroke="#6b7480" strokeWidth="2" />
                          <rect
                            x="16"
                            y="20"
                            width="32"
                            height="8"
                            rx="2"
                            fill="#6b7480"
                            fillOpacity="0.15"
                            stroke="#6b7480"
                            strokeWidth="1.5"
                          />
                          <line
                            x1="22"
                            y1="24"
                            x2="42"
                            y2="24"
                            stroke="#6b7480"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                      <span className={styles.imageSelectLabel}>{option.label}</span>
                    </button>
                  ))}
                </div>
              </FormField>
            )}
          />
          {visibility.showUpliftResistanceConfig && (
            <Controller
              name="upliftResistanceConfig"
              control={form.control}
              render={({ field }) => (
                <FormField name="upliftResistanceConfig"
                  label={t.lockCalculatorModule?.structural?.upliftResistanceConfig}
                  error={form.formState.errors.upliftResistanceConfig}
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.upliftResistanceConfig,
                    onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.upliftResistanceConfig),
                  }}
                >
                  <div className={styles.imageSelectGrid}>
                    <button
                      type="button"
                      className={classNames(styles.imageSelectOption, {
                        [styles.imageSelectOptionSelected]: field.value === 0,
                      })}
                      aria-pressed={field.value === 0}
                      disabled={readOnly}
                      title={t.lockCalculatorModule?.structural?.lateralResistance?.inclinedFasteners}
                      onClick={() => field.onChange(field.value === 0 ? null : 0)}
                    >
                      <img
                        className={styles.imageSelectImage}
                        src={resistanceImages[3]!}
                        alt={t.lockCalculatorModule?.structural?.lateralResistance?.inclinedFasteners}
                      />
                      <span className={styles.imageSelectLabel}>
                        {t.lockCalculatorModule?.structural?.lateralResistance?.inclinedFasteners}
                      </span>
                    </button>
                  </div>
                </FormField>
              )}
            />
          )}

          {/* TODO: definire se la distanza dal bordo è utile oppure no. Da aggiungere, utile per il calcolo. */}
        </div>
        <div className={styles.wizardNav}>
          <button
            type="button"
            className={styles.wizardNavBtn}
            disabled={readOnly || isTriggeringOperation || automaticBlock !== null || !canContinue}
            title={automaticBlock ? noticeText(automaticBlock, t) : undefined}
            ref={generateAllRef}
            onClick={handleGenerateAll}
          >
            {t.lockCalculatorModule?.structural?.generateAllConfigurations}
          </button>
          <button
            type="button"
            className={styles.wizardNavBtn}
            disabled={readOnly || isTriggeringOperation || !canContinue}
            onClick={goToConnectorsStep}
          >
            {t.lockCalculatorModule?.structural?.goToManualConfiguration}
          </button>
        </div>
      </Card>
    </>
  );
};
